// packages/web-ui/src/components/capability-pills/CapabilityPillRow.tsx
//
// Story W3-S6 AC #2 — `<CapabilityPillRow>` renders the canonical row of
// five capability pills (`Git`, `KB`, `DA`, `Sub`, `SSE`) in a fixed order.
// Pure presentational component: data flows from the `useCapabilities()`
// hook into props; click-to-configure callbacks bubble back up to whichever
// AppShell wrapper is responsible (Banner / StatusBar — wired in a follow-up
// integration tick per orchestrator scope).
//
// Polling lifecycle (start / stop on mount / unmount) is owned by the hook.
// This component does NOT subscribe to the store directly.

import type { JSX } from 'react';
import { HealthPill, type HealthPillState } from './HealthPill.js';
import type { CapabilitySnapshot, SseStatus } from '../../state/capabilities.js';

/**
 * Settings categories the pill row can deep-link into. The destination
 * Settings panel (W6-S2) is responsible for scrolling to the matching
 * section once it lands; for now the navigation event is sufficient.
 */
export type CapabilityCategory =
  | 'git'
  | 'kb'
  | 'domain-agents'
  | 'sub-agents'
  | 'syside'
  | 'capabilities';

export interface CapabilityPillRowProps {
  /** Live capability snapshot, or `null` while loading or after a hard error. */
  capabilities: CapabilitySnapshot | null;
  /**
   * Current SSE connection state. Drives the SSE pill colour. Defaults to
   * `'open'` — callers that have wired the SSE slice should pass the live
   * state explicitly.
   */
  sseStatus?: SseStatus;
  /** True while the very first poll is in flight. */
  loading: boolean;
  /** Last poll error, or `null` when the most-recent poll succeeded. */
  error: string | null;
  /**
   * Click handler invoked when a `missing` pill is activated. The category
   * argument identifies which Settings sub-page should be opened.
   */
  onConfigure?: (category: CapabilityCategory) => void;
  /** Optional `data-testid` override for the row container. */
  testId?: string;
}

/** Canonical pill order per UX §3.1 line 148 — DO NOT alphabetise. */
const PILL_ORDER = ['Git', 'KB', 'DA', 'Sub', 'SSE'] as const;

function sseToState(s: SseStatus): HealthPillState {
  switch (s) {
    case 'open':
      return 'ok';
    case 'connecting':
      return 'loading';
    case 'reconnecting':
      return 'warn';
    case 'closed':
      return 'error';
  }
}

export function CapabilityPillRow({
  capabilities,
  sseStatus = 'open',
  loading,
  error,
  onConfigure,
  testId = 'capability-pill-row',
}: CapabilityPillRowProps): JSX.Element {
  // Loading skeleton — render five greyed-out pills with the canonical labels.
  if (loading && capabilities === null) {
    return (
      <div
        data-testid={testId}
        data-mode="loading"
        role="status"
        aria-live="polite"
        className="flex items-center gap-2"
      >
        {PILL_ORDER.map((label) => (
          <HealthPill key={label} label={label} state="loading" />
        ))}
      </div>
    );
  }

  // Hard-error state — collapse to a single Health pill with the error text
  // surfaced via the title tooltip. UX §17 line 2120: "Health endpoint
  // failed. Retry / Open Settings". The Retry / Open Settings affordances
  // are tooltip-only in v1 to keep the row narrow.
  if (error !== null && capabilities === null) {
    return (
      <div
        data-testid={testId}
        data-mode="error"
        role="status"
        aria-live="polite"
        className="flex items-center gap-2"
      >
        <HealthPill
          label="Health"
          state="error"
          tooltip={`Health endpoint failed: ${error}`}
        />
      </div>
    );
  }

  const caps = capabilities;
  // After the initial load we always have a non-null `capabilities`. The
  // explicit narrow keeps TypeScript happy without an assertion.
  if (caps === null) {
    return (
      <div
        data-testid={testId}
        data-mode="empty"
        role="status"
        aria-live="polite"
        className="flex items-center gap-2"
      />
    );
  }

  return (
    <div
      data-testid={testId}
      data-mode="ready"
      role="status"
      aria-live="polite"
      className="flex items-center gap-2"
    >
      <HealthPill
        label="Git"
        state={caps.hasGit ? 'ok' : 'missing'}
        tooltip={
          caps.hasGit
            ? 'Git workspace detected'
            : 'No .git directory — workspace is not git-tracked'
        }
        onConfigure={
          !caps.hasGit && onConfigure !== undefined
            ? () => onConfigure('git')
            : undefined
        }
      />
      <HealthPill
        label="KB"
        state={caps.hasKbConfig ? 'ok' : 'missing'}
        tooltip={
          caps.hasKbConfig
            ? 'KB index loaded'
            : 'No tessa-config.json — initialise KB to enable indexing'
        }
        onConfigure={
          !caps.hasKbConfig && onConfigure !== undefined
            ? () => onConfigure('kb')
            : undefined
        }
      />
      <HealthPill
        label="DA"
        state={caps.hasDomainAgents ? 'ok' : 'missing'}
        tooltip={
          caps.hasDomainAgents
            ? 'Domain agents available (knowledge-base/domain-agents/)'
            : 'No knowledge-base/domain-agents/*.json found'
        }
        onConfigure={
          !caps.hasDomainAgents && onConfigure !== undefined
            ? () => onConfigure('domain-agents')
            : undefined
        }
      />
      <HealthPill
        label="Sub"
        state={caps.hasSubAgents ? 'ok' : 'missing'}
        tooltip={
          caps.hasSubAgents
            ? 'Sub-agents available (.claude/agents/)'
            : 'No .claude/agents/ directory — add agent files to enable sub-agents'
        }
        onConfigure={
          !caps.hasSubAgents && onConfigure !== undefined
            ? () => onConfigure('sub-agents')
            : undefined
        }
      />
      <HealthPill
        label="SSE"
        state={sseToState(sseStatus)}
        tooltip={`SSE: ${sseStatus}`}
      />
    </div>
  );
}
